import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import { ThemeProvider, CacheProvider, createCache, } from '@vitessce/styles';
// eslint-disable-next-line import/no-unresolved
import ZipFileStore from '@zarrita/storage/zip';
import { transformEntriesForZipFileStore } from '@vitessce/zarr-utils';
import { QueryCache, QueryClient, QueryClientProvider, } from '@tanstack/react-query';
import { isEqual } from 'lodash-es';
import { buildConfigSchema, latestConfigSchema } from '@vitessce/schemas';
import { log, setLogLevel, setDebugMode, DEFAULT_LOG_LEVEL, DEFAULT_DEBUG_MODE, } from '@vitessce/globals';
import { muiTheme } from './shared-mui/styles.js';
import { ViewConfigProvider, createViewConfigStore, AuxiliaryProvider, createAuxiliaryStore, } from './state/hooks.js';
import VitessceGrid from './VitessceGrid.js';
import { Warning } from './Warning.js';
import { DebugWindow } from './DebugWindow.js';
import CallbackPublisher from './CallbackPublisher.js';
import { initialize, logConfig, } from './view-config-utils.js';
import { createLoaders } from './vitessce-grid-utils.js';
import { AsyncFunctionsContext } from './contexts.js';
/**
 * The Vitessce component.
 * @param {object} props
 * @param {object} props.config A Vitessce view config.
 * If the config is valid, the VitessceGrid will be rendered as a child.
 * If the config is invalid, a Warning will be rendered instead.
 * @param {undefined|number} props.rowHeight Row height for grid layout. Optional.
 * @param {number} props.height Total height for grid layout. Optional.
 * @param {string} props.theme The theme, used for styling as
 * light or dark. Optional. By default, "dark"
 * @param {function} props.onWarn A callback for warning messages. Optional.
 * @param {function} props.onConfigChange A callback for view config
 * updates. Optional.
 * @param {function} props.onLoaderChange A callback for loader
 * updates. Optional.
 * @param {boolean} props.validateConfig Whether to validate or not. Only to be
 * set to false in controlled component situations, where bypassing validation
 * is required for performance, and the parent knows the config
 * is already valid (e.g., it originated from onConfigChange). By default, true.
 * @param {boolean} props.validateOnConfigChange Whether to validate
 * against the view config schema when publishing changes. Use for debugging
 * purposes, as this may have a performance impact. By default, false.
 * @param {null|string} props.uid A unique identifier for this Vitessce instance,
 * for the purpose of avoiding CSS autogenerated class name conflicts. Must be valid as part
 * of a CSS class name string.
 * @param {undefined|Node} props.styleContainer The DOM node for insertion of styles,
 * passed to Emotion's createCache.container. Useful when Vitessce is in the Shadow DOM.
 * @param {boolean} props.remountOnUidChange Whether to remount the coordination provider
 * upon changes to config.uid. By default, true.
 * @param {array} props.viewTypes Plugin view types.
 * @param {array} props.fileTypes Plugin file types.
 * @param {array} props.jointFileTypes Plugin joint file types.
 * @param {array} props.coordinationTypes Plugin coordination types.
 * @param {null|object} props.warning A warning to render within the Vitessce grid,
 * @param {boolean} props.pageMode Whether to render in page mode. By default, false.
 * @param {boolean} props.debugMode Whether to display the debugWindow. By default, false.
 * @param {null|string} props.logLevel To set the log level in the console.
 * provided by the parent.
 */
export function VitS(props) {
    const { config, stores: storesProp, rowHeight, height, theme, onWarn, onConfigChange, onLoaderChange, validateConfig = true, validateOnConfigChange = false, isBounded = false, uid = null, styleContainer, remountOnUidChange = true, viewTypes: viewTypesProp, fileTypes: fileTypesProp, jointFileTypes: jointFileTypesProp, coordinationTypes: coordinationTypesProp, asyncFunctions: asyncFunctionsProp, warning, pageMode = false, children, debugMode = DEFAULT_DEBUG_MODE, logLevel = DEFAULT_LOG_LEVEL, } = props;
    // eslint-disable-next-line no-unused-vars
    const [debugErrors, setDebugErrors] = useState([]);
    const viewTypes = useMemo(() => (viewTypesProp || []), [viewTypesProp]);
    const fileTypes = useMemo(() => (fileTypesProp || []), [fileTypesProp]);
    const jointFileTypes = useMemo(() => (jointFileTypesProp || []), [jointFileTypesProp]);
    const coordinationTypes = useMemo(() => (coordinationTypesProp || []), [coordinationTypesProp]);
    // Set error handling-related globals.
    useLayoutEffect(() => {
        setLogLevel(logLevel);
    }, [logLevel]);
    useLayoutEffect(() => {
        setDebugMode(debugMode);
    }, [debugMode]);
    const configVersion = config?.version;
    // If config.uid exists, then use it for hook dependencies to detect changes
    // (controlled component case). If not, then use the config object itself
    // and assume the un-controlled component case.
    const configKey = useMemo(() => {
        if (config?.uid) {
            return config.uid;
        }
        // Stringify the config object so it can be used as a key
        // Otherwise, the key will be [object Object]
        return JSON.stringify(config);
    }, [config]);
    const pluginSpecificConfigSchema = useMemo(() => buildConfigSchema(fileTypes, jointFileTypes, coordinationTypes, viewTypes), [viewTypes, fileTypes, jointFileTypes, coordinationTypes]);
    // Pre-create and cache ZipFileStore instances for all zip URLs
    // This ensures that multiple loaders pointing to the same zip URL
    // share the same zip store, preserving HTTP2 multiplexing.
    const mergedStores = useMemo(() => {
        // Note: this logic could alternatively be located inside createLoaders,
        // and could initialize all stores (not only zip stores).
        const result = storesProp ?? {};
        // Collect unique zip URLs and their requestInit from the config
        // Maps URL -> requestInit for store creation with authentication headers
        const urlMap = new Map();
        if (Array.isArray(config.datasets)) {
            config.datasets.forEach((dataset) => {
                dataset.files?.forEach((file) => {
                    if (file.fileType?.endsWith('.zip') && file.url) {
                        // Use the first requestInit encountered for each URL.
                        if (!urlMap.has(file.url)) {
                            urlMap.set(file.url, file.requestInit);
                        }
                    }
                });
            });
        }
        // Create stores for new zip URLs with their requestInit
        urlMap.forEach((requestInit, url) => {
            // Only create a new store if one does not already exist for this URL.
            if (!result[url]) {
                result[url] = ZipFileStore.fromUrl(url, {
                    overrides: requestInit,
                    transformEntries: transformEntriesForZipFileStore,
                });
            }
        });
        return result;
        // TODO: also depend on storesProp here?
    }, [configKey]);
    // Process the view config and memoize the result:
    // - Validate.
    // - Upgrade, if legacy schema.
    // - Validate after upgrade, if legacy schema.
    // - Initialize (based on initStrategy).
    const [configOrWarning, success] = useMemo(() => {
        if (warning) {
            return [warning, false];
        }
        logConfig(config, 'input view config');
        if (!validateConfig) {
            return [config, true];
        }
        const result = latestConfigSchema.safeParse(config);
        if (result.success) {
            const upgradedConfig = result.data;
            logConfig(upgradedConfig, 'parsed view config');
            // Perform second round of parsing against plugin-specific config schema.
            const pluginSpecificResult = pluginSpecificConfigSchema.safeParse(upgradedConfig);
            // Initialize the view config according to the initStrategy.
            if (pluginSpecificResult.success) {
                try {
                    const upgradedConfigWithValidPlugins = pluginSpecificResult.data;
                    const initializedConfig = initialize(upgradedConfigWithValidPlugins, jointFileTypes, coordinationTypes, viewTypes);
                    logConfig(initializedConfig, 'initialized view config');
                    return [initializedConfig, true];
                }
                catch (e) {
                    return [
                        {
                            title: 'Config initialization failed.',
                            unformatted: e.message,
                        },
                        false,
                    ];
                }
            }
            return [
                {
                    title: 'Config validation failed on second pass.',
                    unformatted: pluginSpecificResult.error.message,
                },
                false,
            ];
        }
        return [{
                title: 'Config validation failed on first pass.',
                unformatted: result.error.message,
            }, result.success];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configKey, configVersion, pluginSpecificConfigSchema, warning]);
    const queryClient = useMemo(() => new QueryClient({
        // Reference: https://tanstack.com/query/latest/docs/react/guides/window-focus-refetching
        defaultOptions: {
            queries: {
                refetchOnWindowFocus: false,
                retry: 2,
            },
        },
        // Reference: https://tkdodo.eu/blog/react-query-error-handling#the-global-callbacks
        queryCache: new QueryCache({
            onError: (error) => {
                log.error(error);
                if (onWarn) {
                    onWarn(error.message);
                }
            },
        }),
        // TODO: should the queryClient be shared? Or have an option to be shared
        // (e.g., based on remountOnUidChange)?
    }), [configKey]);
    const asyncFunctions = useMemo(() => ({
        queryClient,
        asyncFunctions: Object.fromEntries(
        // Convert the array of PluginAsyncFunction instances to a mapping
        // from function type strings to async functions.
        asyncFunctionsProp?.map(p => ([p.functionType, p.asyncFunction]))
            || []),
    }), [asyncFunctionsProp, queryClient]);
    const muiCache = useMemo(() => createCache({
        key: uid || 'vit',
        prepend: true,
        container: styleContainer,
    }), [uid, styleContainer]);
    // Emit the upgraded/initialized view config
    // to onConfigChange if necessary.
    useEffect(() => {
        if (success && !isEqual(configOrWarning, config) && onConfigChange) {
            onConfigChange(configOrWarning);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [success, configKey, configOrWarning, onConfigChange]);
    // Initialize the view config and loaders in the global state.
    const createViewConfigStoreClosure = useCallback(() => {
        if (success) {
            const loaders = createLoaders(configOrWarning.datasets, configOrWarning.description, fileTypes, coordinationTypes, mergedStores, queryClient);
            return createViewConfigStore(loaders, configOrWarning);
        }
        // No config found, so clear the loaders.
        return createViewConfigStore(null, null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [success, configKey]);
    // TODO: use in ErrorBoundary fallback.
    // Will probably need to move a lot to a child of VitS
    // so that when the child throws errors the parent can catch.
    if (debugMode && debugErrors.length > 0) {
        return (_jsx(CacheProvider, { value: muiCache, children: _jsx(ThemeProvider, { theme: muiTheme[theme], children: _jsx(DebugWindow, { debugErrors: debugErrors }) }) }));
    }
    return success ? (_jsx(CacheProvider, { value: muiCache, children: _jsx(ThemeProvider, { theme: muiTheme[theme], children: _jsx(QueryClientProvider, { client: queryClient, children: _jsx(ViewConfigProvider, { createStore: createViewConfigStoreClosure, children: _jsx(AuxiliaryProvider, { createStore: createAuxiliaryStore, children: _jsxs(AsyncFunctionsContext.Provider, { value: asyncFunctions, children: [_jsx(VitessceGrid, { pageMode: pageMode, success: success, configKey: configKey, viewTypes: viewTypes, fileTypes: fileTypes, coordinationTypes: coordinationTypes, config: configOrWarning, rowHeight: rowHeight, height: height, theme: theme, isBounded: isBounded, stores: mergedStores, queryClient: queryClient, children: children }), _jsx(CallbackPublisher, { onWarn: onWarn, onConfigChange: onConfigChange, onLoaderChange: onLoaderChange, validateOnConfigChange: validateOnConfigChange, pluginSpecificConfigSchema: pluginSpecificConfigSchema })] }) }) }, (remountOnUidChange ? configKey : undefined)) }) }) })) : (_jsx(CacheProvider, { value: muiCache, children: _jsx(ThemeProvider, { theme: muiTheme[theme], children: _jsx(Warning, { ...configOrWarning }) }) }));
}
//# sourceMappingURL=VitS.js.map